import {
  MainArea,
  SaveBar,
  BottomBar,
  Page,
  SingleColumnLayout,
  Header,
  Button,
  Box,
} from '@salecycle/ui-component-library';
import * as React from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SortableContainer, arrayMove, SortableElement } from 'react-sortable-hoc';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';

import { Selectors as Router } from 'router';
import { Selectors as Campaigns } from 'sections/campaigns/state/campaigns';
import { Site } from 'sections/client-management/entities/site';
import { Actions, Selectors } from 'sections/client-management/services/sites';
import { State } from 'store';
import { isEqual } from 'core/ui/utils/isEqual';
import Row from './row';
import AppTopBar from 'core/ui/components/AppTopBar/AppTopBar';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

interface IStateProps {
  siteId: string;
  /** Undefined when neither updated nor saved site exists in the store (avoid partial payloads). */
  site: Site | undefined;
  hasChanges: boolean;
}

interface IDispatchProps {
  update: (siteId: string, client: Site) => void;
  save: (siteId: string, site: Site) => void;
  discard: (siteId: string) => void;
}

export type IProps = IStateProps & IDispatchProps;

// This visualPriorityIndex and onActiveCampaign logic sucks
// can't think of a better way to do this at the moment

export const SortableCampaignPriorities = SortableContainer(
  ({ campaignPriorities }: { campaignPriorities: string[] }) => {
    let visualPriorityIndex = 1;
    const onActiveCampaign = () => ++visualPriorityIndex;
    return (
      <div>
        {campaignPriorities.map((id, index) => (
          <SortableRow
            id={id}
            key={id}
            index={index}
            visualPriorityIndex={() => visualPriorityIndex}
            onActiveCampaign={onActiveCampaign}
          />
        ))}
      </div>
    );
  },
);

const SortableRow = SortableElement(
  ({
    id,
    visualPriorityIndex,
    onActiveCampaign,
  }: {
    id: string;
    visualPriorityIndex: () => number;
    onActiveCampaign: () => void;
  }) => {
    const priorityNumber = visualPriorityIndex();
    return <Row campaignId={id} priorityNumber={priorityNumber} onActiveCampaign={onActiveCampaign} />;
  },
);

export const CampaignPrioritiser: React.FC<IProps> = props => {
  const { push } = useHistory();
  const { t } = useTranslation('campaigns');

  if (!props.site) {
    return null;
  }

  const onSortEnd = ({ oldIndex, newIndex }) =>
    props.update(props.siteId, { campaignPriorities: arrayMove(props.site.campaignPriorities, oldIndex, newIndex) });

  const save = () => props.save(props.siteId, props.site);

  const discard = () => props.discard(props.siteId);

  const newCampaign = () => push(`/sites/${props.siteId}/campaigns/campaigns/newcampaign/objective`);

  return (
    <MainArea>
      <AppTopBar />
      <Page>
        <Header heading="Organise Live, Test &amp; Draft Campaigns">
          <Button variation="primary" onClick={newCampaign}>
            {t('campaigns:newCampaign')}
          </Button>
        </Header>
        <Box stackItems={true} margin="0 0 2rem 0">
          <Link to={`/sites/${props.siteId}/campaigns/campaigns`}>
            <FontAwesomeIcon icon={faAngleLeft as IconProp} /> Back to Campaigns List
          </Link>
        </Box>
        <SingleColumnLayout>
          <SortableCampaignPriorities
            campaignPriorities={props.site.campaignPriorities}
            onSortEnd={onSortEnd}
            useDragHandle={true}
            axis="y"
          />
        </SingleColumnLayout>
      </Page>
      <BottomBar>
        <SaveBar onSaveButtonClicked={save} enableSaveButton={props.hasChanges} onDiscardButtonClicked={discard} />
      </BottomBar>
    </MainArea>
  );
};

export const mapState = (state: State): IStateProps => {
  const siteId = Router.getSiteId(state);
  const savedSite = Selectors.getEntity(state, siteId);
  const site = Selectors.getUpdatedEntity(state, siteId);
  const baseSite = site ?? savedSite;

  if (!baseSite) {
    return {
      siteId,
      site: undefined,
      hasChanges: false,
    };
  }

  const priorityList = baseSite.campaignPriorities || [];
  const campaignIds = Campaigns.getEntityIds(state);

  // Concatenate the two lists such that it excludes priorityList ids that are not actually in the
  // campaign id list, and appends any missing ids that are
  const allIds = priorityList
    .filter(id => campaignIds.indexOf(id) > -1)
    .concat(campaignIds.filter(id => priorityList.indexOf(id) === -1));

  const hasChanges = savedSite != null && !isEqual(savedSite.campaignPriorities || [], allIds);

  return {
    siteId,
    site: {
      ...baseSite,
      campaignPriorities: allIds,
    },
    hasChanges,
  };
};

const actions: IDispatchProps = {
  update: Actions.updateLocal,
  save: Actions.requestUpdate,
  discard: Actions.discardLocal,
};

export default connect(mapState, actions)(CampaignPrioritiser);
